import {
  InstructionAnnotation,
  annotateAccountDelete,
  annotateAccountSet,
  annotatePayment,
  BaseAnnotation,
  baseAnnotate,
  annotateCheckCreate,
  annotateCheckCancel,
  annotateCheckCash,
  annotateEscrowFinish,
  annotateEscrowCancel,
  annotateEscrowCreate,
  annotateDepositPreauth,
  annotateOfferCancel,
  annotateOfferCreate,
  annotatePaymentChannelClaim,
  annotatePaymentChannelCreate,
  annotatePaymentChannelFund,
  annotateSetRegularKey,
  annotateSignerListSet,
  annotateTrustSet,
} from './annotations'
import attachFlags from './attachFlag'
import { Flag } from './flag'
import Transaction from './types/Transaction'
import TransactionType from './types/TransactionType'

/**
 * Create an annotation for the transaction instructions of a transaction.
 *
 * @throws Throws an error in the passed transaction has an invalid transaction type.
 *
 * @param txn - The transaction to annotate.
 *
 * @returns An annotation of the transaction instructions.
 */
// eslint-disable-next-line complexity -- There is no way to avoid this complexity.
function instructionAnnotation(
  txn: Transaction,
): InstructionAnnotation & { additionalFlags?: Flag[] } {
  const transactionInstructions = txn.tx
  switch (transactionInstructions.TransactionType) {
    case TransactionType.AccountDelete:
      return annotateAccountDelete(transactionInstructions)
    case TransactionType.AccountSet:
      return annotateAccountSet(transactionInstructions)
    case TransactionType.CheckCreate:
      return annotateCheckCreate(transactionInstructions)
    case TransactionType.CheckCancel:
      return annotateCheckCancel(transactionInstructions)
    case TransactionType.CheckCash:
      return annotateCheckCash(transactionInstructions)
    case TransactionType.DepositPreauth:
      return annotateDepositPreauth(transactionInstructions)
    case TransactionType.EscrowCancel:
      return annotateEscrowCancel(transactionInstructions)
    case TransactionType.EscrowFinish:
      return annotateEscrowFinish(transactionInstructions)
    case TransactionType.EscrowCreate:
      return annotateEscrowCreate(transactionInstructions)
    case TransactionType.OfferCancel:
      return annotateOfferCancel(transactionInstructions)
    case TransactionType.OfferCreate:
      return annotateOfferCreate(transactionInstructions)
    case TransactionType.Payment:
      return annotatePayment(transactionInstructions)
    case TransactionType.PaymentChannelClaim:
      return annotatePaymentChannelClaim(transactionInstructions)
    case TransactionType.PaymentChannelCreate:
      return annotatePaymentChannelCreate(transactionInstructions)
    case TransactionType.PaymentChannelFund:
      return annotatePaymentChannelFund(transactionInstructions)
    case TransactionType.SetRegularKey:
      return annotateSetRegularKey(transactionInstructions)
    case TransactionType.SignerListSet:
      return annotateSignerListSet(transactionInstructions)
    case TransactionType.TrustSet:
      return annotateTrustSet(transactionInstructions)
    default:
      throw new Error(`Invalid transaction type: ${txn.tx.TransactionType}`)
  }
}

export type Annotation = BaseAnnotation & InstructionAnnotation

/**
 * Creates a transaction description / annotation.
 * Throws errors if the transaction format is incorrect.
 *
 * @param txn - The transaction to be described.
 * @returns A transaction annotation describing the transaction.
 */
export function annotate(txn: Transaction): Annotation {
  const instructions = instructionAnnotation(txn)
  const base = instructions.additionalFlags
    ? attachFlags(baseAnnotate(txn), instructions.additionalFlags)
    : baseAnnotate(txn)

  return {
    ...base,
    ...instructions,
  }
}
